import { Chip, Tooltip } from "@mui/material";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";

/**
 * ScopeBadge — small Chip that surfaces the scope of an assignment
 * or resource. Three cases:
 *
 *   scope="system"      → "System-wide"    (secondary Chip + globe)
 *   scope="account"     → "Account: {name}" (default Chip + building)
 *   scope="project"     → "Project: {name}" (default Chip + folder)
 *
 * The optional `name` slot carries the account or project label.
 *
 * Uses MUI theme tokens only — the outlined variant sits well with
 * both light and dark themes without extra styling.
 */
export default function ScopeBadge({ scope, name, size = "small", sx }) {
  let label;
  let icon;
  let color = "default";
  let tip;
  if (scope === "system" || scope === "SYSTEM") {
    label = "System-wide";
    icon = <PublicOutlinedIcon fontSize="small" />;
    color = "secondary";
    tip = "Platform scope — applies across every account.";
  } else if (scope === "project" || scope === "PROJECT") {
    label = name ? `Project: ${name}` : "Project scope";
    icon = <FolderOutlinedIcon fontSize="small" />;
    tip = name
      ? `Scoped to project "${name}".`
      : "Scoped to a specific project.";
  } else {
    label = name ? `Account: ${name}` : "Account scope";
    icon = <BusinessOutlinedIcon fontSize="small" />;
    tip = name
      ? `Scoped to account "${name}".`
      : "Scoped to a specific account.";
  }
  const chip = (
    <Chip
      icon={icon}
      label={label}
      size={size}
      color={color}
      variant="outlined"
      sx={{
        maxWidth: 240,
        "& .MuiChip-label": {
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
        ...sx,
      }}
    />
  );
  return tip ? <Tooltip title={tip}>{chip}</Tooltip> : chip;
}
